/* eslint-disable react/no-array-index-key */
import { type FC, useEffect, useRef, useState } from 'react';
import Marquee from 'react-fast-marquee';

import Button from 'molecules/Button';
import Heading from 'molecules/Heading';
import RichText from 'molecules/RichText';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import ScrollFadeSection from 'components/Animations/ScrollFadeSection';

import { hasArrayValues } from 'utils/array/arrays';

import type { CardCarouselConfig } from 'components/CardCarousel/config';

const UseCaseTile: FC<{
  title?: string;
  description?: string;
  buttonLabel?: string;
  buttonHref?: string;
  onHoverChange?: (_value: boolean) => void;
  minHeight?: number;
}> = ({ title, description, buttonLabel, buttonHref, onHoverChange, minHeight }) => {
  const handleClick = () => {
    if (buttonHref) {
      window.location.href = buttonHref;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const hasButton = Boolean(buttonHref);

  return (
    <div
      role="button"
      tabIndex={0}
      onMouseEnter={() => onHoverChange?.(true)}
      onMouseLeave={() => onHoverChange?.(false)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={title}
      style={minHeight ? { minHeight: `${minHeight}px` } : undefined}
      className={`flex h-auto cursor-pointer flex-row items-center gap-6 rounded-lg bg-gray-1200 p-5 backdrop-blur-md transition-transform hover:scale-[1.02] md:flex-col md:items-start md:gap-6 md:py-10${
        hasButton ? ' justify-between' : ''
      }`}
    >
      {title && (
        <div className="flex w-full flex-col gap-2.5">
          <h3 className="text-[20px] font-semibold leading-[30px] text-white md:text-xl md:leading-[30px]">{title}</h3>
        </div>
      )}

      {description && (
        <div className="hidden md:block">
          <RichText text={description} className="text-[16px] leading-6 text-gray-300 [&_a]:underline" />
        </div>
      )}

      {buttonHref && (
        <>
          <div className="ml-auto md:hidden">
            <Button
              aria-label={title ? `${buttonLabel} about ${title}` : buttonLabel}
              href={buttonHref}
              hierarchy="linkGray"
              endIcon="arrow-up-right"
              className="pointer-events-none w-fit"
              size={32}
            />
          </div>

          <div className="mt-auto hidden md:block">
            <Button
              aria-label={title ? `${buttonLabel} about ${title}` : buttonLabel}
              label={buttonLabel}
              href={buttonHref}
              hierarchy="linkGray"
              className="pointer-events-none w-fit"
              endIcon="arrow-up-right"
            />
          </div>
        </>
      )}
    </div>
  );
};

const CardCarousel: FC<CardCarouselConfig> = props => {
  const {
    sectionProps,
    rest: { items = [], ...headingProps },
  } = splitSectionProps(props);

  const [isPaused, setIsPaused] = useState(false);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);

  const measureRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!measureRef.current || !hasArrayValues(items)) {
      return;
    }

    const timer = setTimeout(() => {
      if (!measureRef.current) {
        return;
      }

      const tiles = Array.from(measureRef.current.querySelectorAll<HTMLElement>('[data-measure]'));

      const max = tiles.reduce((acc, el) => {
        const h = el.offsetHeight;

        return h > acc ? h : acc;
      }, 0);

      if (max > 0) {
        setMaxHeight(max);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [items]);

  const extendedItems = hasArrayValues(items) ? [...items, ...items, ...items, ...items] : [];

  return (
    <Section {...sectionProps}>
      <div className="flex flex-col gap-10">
        <Heading {...headingProps} alignment="leftInline" />

        <div aria-hidden="true" className="pointer-events-none absolute opacity-0" ref={measureRef}>
          {items.map(item => (
            <div key={item.id} data-measure className="w-[227px]">
              <div className="flex h-auto flex-col items-start gap-6 rounded-lg bg-gray-1200 p-5 md:py-10">
                {item.title && (
                  <div className="flex w-full flex-col gap-2.5">
                    <h3 className="text-[20px] font-semibold leading-[30px] text-white md:text-xl md:leading-[30px]">
                      {item.title}
                    </h3>
                  </div>
                )}

                {item.description && (
                  <div>
                    <RichText text={item.description} className="text-[16px] leading-6 text-gray-300" />
                  </div>
                )}

                {item.buttonHref && (
                  <div className="mt-auto">
                    <Button
                      label={item.buttonLabel}
                      href={item.buttonHref}
                      hierarchy="linkGray"
                      className="w-fit"
                      endIcon="arrow-up-right"
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <ScrollFadeSection delay={0.15}>
          <div className="md:hidden">
            <div className="bg-linear-gradient-carousel-top flex flex-col gap-5">
              {items.map(item => (
                <UseCaseTile
                  key={item.id}
                  title={item.title}
                  description={undefined}
                  buttonLabel={item.buttonLabel}
                  buttonHref={item.buttonHref}
                />
              ))}
            </div>
          </div>

          <div className="hidden md:block">
            <div className="bg-linear-gradient-carousel overflow-hidden">
              <Marquee speed={30} gradient gradientColor="#101017" gradientWidth="135px" play={!isPaused}>
                {extendedItems.map((item, idx) => (
                  <div key={`${item.id}-${idx}`} className="mx-2.5 my-2 w-[227px]">
                    <UseCaseTile
                      title={item.title}
                      description={item.description}
                      buttonLabel={item.buttonLabel}
                      buttonHref={item.buttonHref}
                      onHoverChange={setIsPaused}
                      minHeight={maxHeight || undefined}
                    />
                  </div>
                ))}
              </Marquee>
            </div>
          </div>
        </ScrollFadeSection>
      </div>
    </Section>
  );
};

export default CardCarousel;
